// Pure parser for the Bulk ZIP Upload dialog. Extracted from ZipCoveragePage
// so the replace-expansion logic can be unit-tested without rendering the page.

export function parseBulkZipCsv(csvText, {
  territories = [],
  availableVerticals = [],
  zipsByTerritoryVertical = {},
} = {}) {
  const lines = (csvText || '').split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    return {
      parsed: null,
      error: lines.length === 1 ? 'CSV needs a header row and at least one data row.' : '',
    };
  }

  const headerLine = lines[0];
  const sep = headerLine.includes('\t') ? '\t' : ',';
  const headers = headerLine.split(sep).map((h) => h.trim().toLowerCase().replace(/['"]/g, ''));
  const tIdx = headers.findIndex((h) => h === 'territory');
  const vIdx = headers.findIndex((h) => h === 'vertical');
  const zIdx = headers.findIndex((h) => h === 'zip' || h === 'zip_code' || h === 'zipcode');
  const aIdx = headers.findIndex((h) => h === 'action');

  if (tIdx === -1 || zIdx === -1) {
    return {
      parsed: null,
      error: 'CSV must have "Territory" and "ZIP" columns. "Vertical" and "Action" are optional. Action: add | remove | replace.',
    };
  }

  const territoryMap = {};
  territories.forEach((t) => { territoryMap[t.name.toLowerCase()] = t; });
  const verticalMap = {};
  availableVerticals.forEach((v) => { verticalMap[v.vertical.toLowerCase()] = v; });

  const errors = [];
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(sep).map((c) => c.trim().replace(/^["']|["']$/g, ''));
    const territoryName = cols[tIdx] || '';
    const verticalName = vIdx !== -1 ? (cols[vIdx] || '') : '';
    const rawZip = cols[zIdx] || '';
    const zip = /^\d{1,4}$/.test(rawZip) ? rawZip.padStart(5, '0') : rawZip;
    const action = aIdx !== -1 ? (cols[aIdx] || 'add').toLowerCase() : 'add';

    if (!zip) continue;

    const tMatch = territoryMap[territoryName.toLowerCase()];
    if (!tMatch) {
      const validNames = Object.keys(territoryMap);
      const suggestions = validNames
        .filter((n) => n.includes(territoryName.toLowerCase()) || territoryName.toLowerCase().includes(n))
        .slice(0, 3)
        .map((n) => territoryMap[n].name);
      errors.push({ row: i + 1, field: 'Territory', value: territoryName, suggestions });
      continue;
    }

    let vMatch = null;
    if (verticalName) {
      vMatch = verticalMap[verticalName.toLowerCase()];
      if (!vMatch) {
        const validVNames = Object.keys(verticalMap);
        const suggestions = validVNames
          .filter((n) => n.includes(verticalName.toLowerCase()) || verticalName.toLowerCase().includes(n))
          .slice(0, 3)
          .map((n) => verticalMap[n].vertical);
        errors.push({ row: i + 1, field: 'Vertical', value: verticalName, suggestions });
        continue;
      }
    }

    const normalizedAction = action === 'remove' || action === 'replace' ? action : 'add';
    rows.push({
      territory: tMatch,
      vertical: vMatch,
      zip,
      action: normalizedAction,
      sourceRow: i + 1,
    });
  }

  const groupKey = (r) => `${r.territory.id}__${r.vertical?.id || 'auto'}`;
  const groups = {};
  rows.forEach((r) => {
    const key = groupKey(r);
    if (!groups[key]) {
      groups[key] = {
        territory: r.territory,
        vertical: r.vertical,
        adds: [],
        removes: [],
        replaceRows: [],
        mode: 'add',
      };
    }
    if (r.action === 'remove') {
      groups[key].removes.push(r.zip);
    } else if (r.action === 'replace') {
      groups[key].replaceRows.push(r);
      groups[key].mode = 'replace';
    } else {
      groups[key].adds.push(r.zip);
    }
  });

  // Expand replace groups: desired final set = union of replace + add rows;
  // anything currently in (territory, vertical) not in that set becomes a remove.
  Object.values(groups).forEach((g) => {
    if (g.mode !== 'replace') return;
    if (!g.vertical) {
      const rowNums = g.replaceRows.map((r) => r.sourceRow).slice(0, 5).join(', ');
      errors.push({
        row: rowNums,
        field: 'Vertical',
        value: '(missing)',
        suggestions: [],
        message: 'Replace rows require a Vertical so we know which existing set to replace.',
      });
      return;
    }
    const desired = new Set([
      ...g.replaceRows.map((r) => r.zip),
      ...g.adds,
    ]);
    const current = zipsByTerritoryVertical[`${g.territory.id}__${g.vertical.id}`] || [];
    const currentSet = new Set(current.map((czv) => czv.zip));
    g.adds = [...desired].filter((z) => !currentSet.has(z));
    // Explicit removes win only if the ZIP isn't also in desired; otherwise the
    // user contradicted themselves and the replace-set is the source of truth.
    const explicitRemoves = new Set(g.removes.filter((z) => !desired.has(z)));
    const impliedRemoves = current
      .map((czv) => czv.zip)
      .filter((z) => !desired.has(z) && !explicitRemoves.has(z));
    g.removes = [...explicitRemoves, ...impliedRemoves];
    g.replaceCount = g.replaceRows.length;
  });

  return {
    parsed: {
      groups: Object.values(groups),
      errors,
      totalRows: rows.length,
    },
    error: errors.length > 0
      ? `${errors.length} row${errors.length > 1 ? 's' : ''} have unmatched territory/vertical names.`
      : '',
  };
}
